/**
 * Hero: title plus 3-4 compact stats. Used on the student dashboard and the admin overview only.
 * Each stat is one short label and one short value; nothing wraps or overflows.
 * HeroSkeleton has the same dimensions as Hero so nothing shifts when data arrives.
 */
import type { ReactNode } from "react";
import { Skeleton } from "./Skeleton";
import { ProgressRing } from "./ProgressRing";

export type HeroStatItem = {
  /** Keep to one or two words (e.g. "Chapters", "Quizzes"). */
  label: string;
  /** Short value (e.g. "4/12", "Premium"). */
  value: ReactNode;
  /** Optional detail, shown from tablet width up and as a tooltip. Keep it short. */
  hint?: string;
  icon?: ReactNode;
  /** 0-100: shows a small ring instead of the icon. */
  progress?: number;
  /** True while this stat's data is still loading: shows a skeleton of the same size. */
  loading?: boolean;
  onClick?: () => void;
};

const TILE = "flex min-h-[4.5rem] min-w-0 items-center gap-3 rounded-tile bg-soft px-3 py-2.5 text-left";

function Lead({ s }: { s: HeroStatItem }) {
  if (s.loading) return <Skeleton className="h-9 w-9 shrink-0 rounded-full" />;
  if (s.progress != null) return <ProgressRing value={s.progress} label={s.label} size={36} stroke={4} />;
  if (s.icon)
    return (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-primary" aria-hidden="true">
        {s.icon}
      </span>
    );
  return null;
}

function StatBody({ s }: { s: HeroStatItem }) {
  return (
    <>
      <Lead s={s} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-muted-foreground">{s.label}</span>
        {s.loading ? (
          <Skeleton className="mt-1 h-6 w-14" />
        ) : (
          <span className="block truncate font-display text-xl font-bold leading-tight text-foreground">{s.value}</span>
        )}
        {s.hint && !s.loading && <span className="hidden truncate text-xs text-muted-foreground md:block">{s.hint}</span>}
      </span>
    </>
  );
}

export function Hero({ title, subtitle, actions, stats }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; stats: HeroStatItem[] }) {
  return (
    <section className="space-y-3 rounded-card border border-border bg-card p-4 shadow-soft sm:p-5" aria-label="Overview">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-bold tracking-tight text-foreground sm:text-2xl">{title}</h1>
          {subtitle ? <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{subtitle}</p> : null}
        </div>
        {actions ? <div className="shrink-0">{actions}</div> : null}
      </div>
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {stats.map((s) =>
          s.onClick && !s.loading ? (
            <button key={s.label} type="button" title={s.hint} onClick={s.onClick} className={`${TILE} transition-colors hover:bg-lift focus-visible:ring-2 ring-ring`}>
              <StatBody s={s} />
            </button>
          ) : (
            <div key={s.label} title={s.hint} className={TILE}>
              <StatBody s={s} />
            </div>
          ),
        )}
      </div>
    </section>
  );
}

export function HeroSkeleton({ stats = 4 }: { stats?: number }) {
  return (
    <section className="space-y-3 rounded-card border border-border bg-card p-4 shadow-soft sm:p-5" aria-busy="true" aria-label="Loading overview">
      <div className="space-y-2">
        <Skeleton className="h-7 w-48 max-w-full" />
        <Skeleton className="h-4 w-64 max-w-full" />
      </div>
      <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        {Array.from({ length: stats }).map((_, i) => (
          <div key={i} className={TILE}>
            <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
            <span className="min-w-0 flex-1 space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-6 w-14" />
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
